import type { ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * Figma component "Badge": radius/xs 6 px, 8 px padding, DLZ/UI/Label trimmed to cap height (25 px).
 *   sale  bg/brand + text/on-brand ("-20 %", "Ušetríte 18 €")
 *   new   bg/inverse + text/inverse ("Novinka")
 */
const TONES = {
  sale: 'bg-[#D7191F] text-white',
  new: 'bg-[#0F1012] text-white',
} as const;

export type BadgeTone = keyof typeof TONES;

export function Badge({
  tone,
  children,
  className,
}: {
  tone: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn('dlz-label inline-flex shrink-0 rounded-[6px] p-2', TONES[tone], className)}
    >
      <span className="dlz-trim">{children}</span>
    </span>
  );
}

/*
 * Figma component "Stock status": 8 px dot + DLZ/Body/S Medium, gap 8.
 *   in-stock      status/success dot, text/success
 *   on-order      status/warning dot, text/warning
 *   out-of-stock  icon/disabled dot, text/tertiary
 */
const STOCK = {
  'in-stock': { dot: 'bg-[#4DB873]', text: 'text-[#16753A]' },
  'on-order': { dot: 'bg-[#F0B23E]', text: 'text-[#9A5F00]' },
  'out-of-stock': { dot: 'bg-[#9096A0]', text: 'text-[#6B7079]' },
} as const;

export type StockTone = keyof typeof STOCK;

export function StockStatus({
  tone,
  children,
  className,
}: {
  tone: StockTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'dlz-body-s-medium inline-flex items-center gap-2',
        STOCK[tone].text,
        className,
      )}
    >
      <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', STOCK[tone].dot)} />
      {children}
    </span>
  );
}
